<template>
  <div>
    <Banner3 class="Docs" image="/assets/background/cain.jpg" imagepos="0, -200px">
      <template slot="title">Documentation</template>
      <template slot="caption">Stay awhile and listen. All the information you need to bring your Warcraft III community to life with an all-you-can-eat buffet of data-driven stats! If you find an error in the documentation or have a feature-request, feel free to drop a message in <router-link :to="{ name: 'Discord' }">our discord</router-link>.</template>
    </Banner3>

    <div class="container container--ultrawide d-flex align-items-start p-rel">
      <Sidebar>
        <router-link :to="{ name: 'Documentation', params: { page: 'about' } }" :class="{ 'active' : $route.params.page == 'about' }">About</router-link>

        <Scrollbar v-if="$route.params.page == 'about'">
          <a href="#features">Features</a>
          <a href="#how-it-works">How it Works</a>
          <a href="#faq">FAQ</a>
        </Scrollbar>

        <router-link :to="{ name: 'Documentation', params: { page: 'api' } }" :class="{ 'active' : $route.params.page == 'api' }">API</router-link>

        <Scrollbar v-if="$route.params.page == 'api'">
          <a href="#/">/</a>
          <a href="#/gamelist">/gamelist</a>
          <a href="#/upload">/upload</a>
          <!-- <a href="#/upload/{key}">GET /upload/{key}</a> -->
          <a href="#/replays">/replays</a>
          <!-- <a href="#/replays/{id}">GET /replays/{id}</a> -->
          <!-- <a href="#/replays/{id}/download">GET /replays/{id}/download</a> -->
          <a href="#/maps">/maps</a>
          <!-- <a href="#/maps/{name}">GET /maps/{name}</a> -->
          <!-- <a href="#/results/{replayId}">GET /results/{replayId}</a> -->
          <a href="#/results">/results</a>
          <!-- <a href="#/profiles/{name}">GET /profiles/{name}</a> -->
          <a href="#/profiles">/profiles</a>
          <!-- <a href="#/profiles/{name}/{profileId}">GET /profiles/{name}/{profileId}</a> -->
          <!-- <a href="#/profiles/{name}/{profileId}/performance">GET /profiles/{name}/{profileId}/performance</a> -->
          <a href="#/leaderboard">/leaderboard</a>
          <!-- <a href="#/stats/{id}">GET /stats/{id}</a> -->
          <a href="#/stats">/stats</a>
          <a href="#/compare">/compare</a>
        </Scrollbar>

        <router-link :to="{ name: 'Documentation', params: { page: 'w3mmd' } }" :class="{ 'active' : $route.params.page == 'w3mmd' }">W3MMD Guide</router-link>

        <Scrollbar v-if="$route.params.page == 'w3mmd'">
          <a href="#installation">Installation</a>
          <a href="#variables">Variables</a>
          <a href="#flags">Flags</a>
          <a href="#values">Values</a>
          <a href="#events">Events</a>
        </Scrollbar>
      </Sidebar>

      <Panel2 class="flex-grow-1 Article" v-if="$route.params.page == 'about'">
        <h2>About</h2>

        <p>wc3stats.com aims to offer the latest and greatest in Warcraft III statistics and replay parsing technology. This article describes what it does, how it can be used and who its for.</p>

        <h3 id="features">Features</h3>

        <ul>
          <li>A custom-built Warcraft III replay parser which implements the entire native replay standard as well as the W3MMD and DoTA custom standards. </li>
          <li>A full-featured <router-link :to="{ name: 'Documentation', params: { page: 'api' } }" :class="{ 'active' : $route.params.page == 'api' }">API</router-link> for accessing all raw and processed data (available for external use).</li>
          <li>Games Profiles: Players, APM chart, custom data (W3MMD if map supported), events (if map supported) and chatlog.</li>
          <li>Map Profiles: Aggregated statistics, automatically generated ELO-based leaderboard (alternative algorithms available) and game archive.</li>
          <li>Player Profiles: Aggregated statistics, rating history, peers and game archive.</li>
          <li>Support for different map modes.</li>
          <li><a href="https://github.com/wc3stats/auto-uploader#installation" target="_blank">Automatic replay uploads</a>.</li>
          <li><a href="https://github.com/wc3stats/scantron-9000" target="_blank">Discord bot</a> to automatically upload replays posted in your Discord server.</li>
        </ul>

        <h3 id="how-it-works">How it Works</h3>

        <p>
          When replays are uploaded to the site, they are parsed, stored in a replay database and added to a processing queue. When duplicate uploads are detected, the chatlogs are merged and the longer of the two replays is taken as the master copy. When processing, statistics are generated for the map played on and players in-game. There are several special keys which the processor has been configured to use.<br><br>

          Periodically, the entire replay database is reprocessed from scratch to ensure any changes to the processor are properly reflected in the data.
        </p>

        <h3 id="faq">FAQ</h3>

        <p>
          <strong>Whats the point? Replay parsers already exist...</strong><br><br>

          Using WC3Stats allows mappers and community leaders to focus less on how to store and process replay information and more on making their maps fun! The WC3Stats developers, while hobbyist, focus solely on making features that can be applied to all maps. When new stuff is released, all mappers get access to it without having to develop anything themselves.<br><br>

          Since parsing and processing has been completely abstracted away through the API, you also don't have to worry about any nuance in parsing or changes in the replay format. We will update it once and it will work for everyone!<br><br>

          <strong>I already have a community website, can I still use wc3stats.com?</strong><br><br>

          Absolutely! We impose practically no regulation on how the data (processed or raw) is used. All API endpoints are available for use, you may show as much or as little data on your own site as you want. Using WC3Stats instead of your own parser rids you the headache of having to parse, process and store all of that data.<br><br>

            We also provide solutions for easy replay collection via our <a href="https://github.com/wc3stats/scantron-9000" target="_blank">Discord bot</a> and <a href="https://github.com/Komodo123/wc3stats-auto-uploader" target="_blank">Automatic replay uploads</a>.<br><br>

          <strong id="rating-system">How does the rating system work?</strong><br><br>

          The rating system (and statistics aggregation) is automatically activated when a game is detected as "ratable". To make games ratable, the mapper must <router-link :to="{ name: 'Documentation', params: { page: 'w3mmd' } }" :class="{ 'active' : $route.params.page == 'w3mmd' }">implement W3MMD</router-link> and send the special keys "team" and either "placement" or "score" for each player. Team is used to determine which players should be grouped together. Teams are sorted by (in order of decreasing precedence) if they have a win flag, placement or score depending on which is sent. These keys make a game rated!<br><br>

          The actual rating system is modular and custom algorithms can be requested. Currently the only supported algorithms are linear (based on placement and the player's league) and a modified version of ELO to extend to more than two teams. Glicko2 may be implemented in the future as a modern alternative to ELO. New maps default to using ELO which is best for most games where a higher rating suggests a higher chance of winning. All rating parameters are configurable upon request.<br><br>

          <strong>How do I get a map profile page?</strong><br><br>

          Simply ask for one! There are virtually no restrictions on what maps are allowed to have a profile page. The only requirements are:

          <ol>
            <li>The mapper must be registered in the <a href="https://discord.gg/N3VGkUM" target="_blank">wc3stats.com Discord</a>.</li>
            <li>The map must have ratable games (see <a href="#rating-system">how to make games ratable</a>.</li>
            <li>The map must not depict any explicit content.</li>
          </ol>
        </p>
      </Panel2>

      <Panel2 class="flex-grow-1 Article" v-if="$route.params.page == 'api'">
        <h2>API</h2>
        <strong>Version</strong>: 1.4<br><br>
        <strong>Host</strong>: https://api.wc3stats.com<br><br>

        <p>

<code>{
  status:     # OK or FAIL,
  code:       # HTTP status code,
  queryTime:  # Time to query (sec),
  pagination: # Large requests may be paginated. This key stores the paging details.
  body:       # Payload
}</code>

          All requests marked with an asterisk (*) support the following query string parameters:

<code>&sort=&lt;string&gt;
&order=asc|desc
&limit=&lt;int&gt;
&page=&lt;int&gt;</code>

          All requests marked with two asterisks (**) further support the follow case-sensitive query string parameters:

<code>&map=&lt;string&gt;
&ladder=&lt;string&gt;
&season=&lt;string&gt;
&round=&lt;string&gt;
&mode=&lt;string&gt;</code>

          Note for Battle Tag<br><br>

          For player names with battle tags (e.g. <mark>Anders#1234</mark>), replace the pound with <mark>%23</mark> (e.g. <mark>Anders%231234</mark>). For example, instead of <mark>/profiles/Anders#1234</mark>, use <mark>/profiles/Anders%231234</mark>.
        </p>

<Endpoint route="/" method="GET" description="Health check" example="/" id="/">{
  "status":"OK",
  "code":200,
  "queryTime":0.0058,
  "body":{
    "date":"Tue Nov 12 07:12:40 UTC 2019",
    "uptime":"up 5 weeks, 1 day, 4 hours, 19 minutes"
  }
}</Endpoint>

<Endpoint route="/gamelist" method="GET" description="Get a list of currently hosted games on Battle.net" example="/gamelist" id="/gamelist">{
   "status":"OK",
   "code":200,
   "queryTime":0.0009,
   "body":[
      {
         "id":430760,
         "name":"LINE TOWER WARS ",
         "server":"eu",
         "host":"seka#21355",
         "map":"LineTowerWarsR_4.2eP.w3x",
         "created":1613176643,
         "firstSeen":1613176667,
         "uptime":3173,
         "slotsTaken":1,
         "slotsTotal":15
      },
      {
         "id":74785,
         "name":"legion ranked",
         "server":"usw",
         "host":"Juanjo262#1608",
         "map":"Legion-TD-5.0_Team_OZE-rc9.w3x",
         "created":1613176885,
         "firstSeen":1613177295,
         "uptime":2931,
         "slotsTaken":10,
         "slotsTotal":10
      },
      {
         "id":207585,
         "name":"fight of characters",
         "server":"usw",
         "host":"leongyaomun#1247",
         "map":"FOCS3 Another 9.7 B01.w3x",
         "created":1613177543,
         "firstSeen":1613177547,
         "uptime":2273,
         "slotsTaken":1,
         "slotsTotal":12
      },
      ...
  ]
}</Endpoint>

<Endpoint route="/upload" method="POST" description="Upload a replay. Expects multipart/form-data file upload. Returns the fully parsed replay object on success." example="/upload" id="/upload">See GET /replays/{id}</Endpoint>
<Endpoint route="/upload/{key}" method="POST" description="Upload a replay to a ladder. The key must match the ladder configuration exactly." example="/upload">See GET /replays/{id}</Endpoint>

<Endpoint route="/replays" method="GET" description="Get list of replays matching query filters. <code class='mb-0'>/replays
      &search=<query> # Name or map case insensitive substring.
      &for=<string>    # Name of player who must be in the replay.
      &chat=<integer>   # 0 for off (default), 1 to search chatlogs for &search parameter (along with map or name) or 2 to only search chatlogs.
</code>" example="/replays&chat=1&search=Broken%20Alliances&page=6&limit=1&sort=playedOn&order=desc" id="/">{
   "status":"OK",
   "code":200,
   "queryTime":1.0211,
   "pagination":{
      "next":7,
      "before":5,
      "first":1,
      "current":6,
      "last":7990,
      "perPage":1,
      "totalPages":7990,
      "totalItems":7990
   },
   "body":[
      {
         "name":"broken alliances - ne1 ally?",
         "map":"Broken Alliances",
         "variant":null,
         "flags":76,
         "length":2429,
         "playedOn":1613164068,
         "ladder":"Public",
         "season":"Season 4",
         "processed":true,
         "isVoid":false,
         "id":91501,
         "players":[
            {
               "colour":0,
               "name":"Cloudhunt649#1808"
            },
            {
               "colour":1,
               "name":"napå#2146"
            },
            {
               "colour":2,
               "name":"OldSchoolVA#11765"
            },
            {
               "colour":3,
               "name":"blupark103#11870"
            },
            {
               "colour":4,
               "name":"MythǂIcarus#1944"
            },
            {
               "colour":5,
               "name":"Eversor#21171"
            },
            {
               "colour":6,
               "name":"BIGUP#11270"
            },
            {
               "colour":7,
               "name":"Esher#11516"
            },
            {
               "colour":8,
               "name":"Grimm#23408"
            },
            {
               "colour":9,
               "name":"peeweeking#3649"
            },
            {
               "colour":10,
               "name":"aMaYa#21878"
            },
            {
               "colour":11,
               "name":"HappyFeet#19992"
            }
         ]
      }
   ]
}</Endpoint>

<Endpoint route="/replays/{id}" method="GET" description="Get replay data by ID." example="/replays/91584" id="/replays">{
   "status":"OK",
   "code":200,
   "queryTime":0.0031,
   "body":{
      "hash":"634bbfaf66a646c3cd11db2bfcf89626",
      "name":"Broken Alliances !!",
      "map":"Broken Alliances",
      "variant":null,
      "flags":76,
      "file":"https://wc3stats.s3.amazonaws.com/replays/634bbfaf66a646c3cd11db2bfcf89626/Miracle%231425-Broken%20Alliances%20%21%21.w3g",
      "size":920105,
      "length":2280,
      "playedOn":1613176339,
      "ladder":"Public",
      "season":"Season 4",
      "keys":[
         {
            "name":"ArkhanIV#1925",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"Miracle#1425",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"peeweeking#3649",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"CatchYaLater#1458",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"FluffDynasty#1230",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"sumre#2728",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"napå#2146",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"SaGe#13105",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"dangrash#21222",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"Negromanser#1112",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"BlindMoOse#21102",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"Bu77man#21643",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"Global"
            }
         },
         {
            "name":"ArkhanIV#1925",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"Miracle#1425",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"peeweeking#3649",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"CatchYaLater#1458",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"FluffDynasty#1230",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"sumre#2728",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"napå#2146",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"SaGe#13105",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"dangrash#21222",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"Negromanser#1112",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"BlindMoOse#21102",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         },
         {
            "name":"Bu77man#21643",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 4",
               "round":"Global",
               "mode":"1 Ally"
            }
         }
      ],
      "processed":true,
      "uploads":[
         {
            "saver":"Miracle#1425",
            "file":"https://wc3stats.s3.amazonaws.com/replays/634bbfaf66a646c3cd11db2bfcf89626/Miracle%231425-Broken%20Alliances%20%21%21.w3g",
            "size":920105,
            "timestamp":1613176339
         }
      ],
      "data":{
         "header":{
            "intro":"Warcraft III recorded game",
            "headerSize":68,
            "compressedSize":920105,
            "headerVersion":1,
            "uncompressedSize":2083906,
            "numBlocks":255,
            "identification":"PX3W",
            "majorVersion":10032,
            "buildVersion":6112,
            "flags":32768,
            "length":2280,
            "checksum":2020725675
         },
         "game":{
            "name":"Broken Alliances !!",
            "speed":2,
            "visibility":3,
            "observers":0,
            "teamsTogether":true,
            "lockedTeams":true,
            "fullShare":false,
            "randomHero":false,
            "randomRaces":false,
            "checksum":1828788482,
            "map":"Broken Alliances 6.4c.w3x",
            "host":"Negromanser#1112",
            "numSlots":12,
            "type":1,
            "isLocal":null,
            "private":32,
            "recordId":25,
            "recordLength":115,
            "slotRecords":12,
            "players":[
               {
                  "type":0,
                  "id":1,
                  "name":"Negromanser#1112",
                  "partial":null,
                  "race":1,
                  "isHost":true,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":0,
                  "order":0,
                  "colour":0,
                  "handicap":100,
                  "leftAt":898,
                  "stayPercent":39.39,
                  "team":0,
                  "apm":113,
                  "activity":[
                     91,
                     67,
                     106,
                     131,
                     76,
                     117,
                     100,
                     107,
                     97,
                     105,
                     123,
                     134,
                     165,
                     153,
                     109
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"None",
                     "heroKills":0,
                     "kills":52,
                     "main":"Horsefields",
                     "score":52,
                     "repicked":"",
                     "burns":0,
                     "mode":"1 Ally",
                     "perks":"Forger, Voodoo, Regenerator",
                     "team":1,
                     "hero":"Tribal Chief",
                     "mainsBurnt":0,
                     "repickedTo":"",
                     "gold":1412
                  }
               },
               {
                  "type":22,
                  "id":2,
                  "name":"BlindMoOse#21102",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":1,
                  "order":1,
                  "colour":1,
                  "handicap":100,
                  "leftAt":2264,
                  "stayPercent":99.3,
                  "team":1,
                  "apm":0,
                  "activity":[
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"None",
                     "heroKills":0,
                     "kills":24,
                     "main":"",
                     "score":24,
                     "repicked":"",
                     "burns":0,
                     "mode":"1 Ally",
                     "perks":"",
                     "team":2,
                     "hero":"Queen of the Dark",
                     "mainsBurnt":0,
                     "repickedTo":"",
                     "gold":894
                  }
               },
               {
                  "type":22,
                  "id":4,
                  "name":"dangrash#21222",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":2,
                  "order":2,
                  "colour":2,
                  "handicap":100,
                  "leftAt":1398,
                  "stayPercent":61.32,
                  "team":1,
                  "apm":62,
                  "activity":[
                     24,
                     55,
                     38,
                     80,
                     41,
                     40,
                     59,
                     63,
                     40,
                     41,
                     51,
                     75,
                     89,
                     83,
                     80,
                     84,
                     71,
                     116,
                     72,
                     81,
                     78,
                     68,
                     32,
                     11
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"None",
                     "heroKills":0,
                     "kills":55,
                     "main":"Centrax",
                     "score":55,
                     "repicked":"King of the Dwarves",
                     "burns":0,
                     "mode":"1 Ally",
                     "perks":"Preserver, Builder, Looter, Recruiter, Defender",
                     "team":3,
                     "hero":"King of the Dwarves",
                     "mainsBurnt":0,
                     "repickedTo":"The Holy Knight",
                     "gold":1436
                  }
               },
               {
                  "type":22,
                  "id":8,
                  "name":"peeweeking#3649",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":3,
                  "order":3,
                  "colour":3,
                  "handicap":100,
                  "leftAt":2261,
                  "stayPercent":99.17,
                  "team":1,
                  "apm":101,
                  "activity":[
                     45,
                     76,
                     87,
                     119,
                     59,
                     105,
                     103,
                     90,
                     56,
                     129,
                     99,
                     111,
                     102,
                     141,
                     135,
                     140,
                     158,
                     142,
                     108,
                     122,
                     102,
                     60,
                     99,
                     109,
                     120,
                     126,
                     67,
                     120,
                     138,
                     81,
                     57,
                     57,
                     80,
                     105,
                     107,
                     133,
                     99,
                     42
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"None",
                     "heroKills":0,
                     "kills":265,
                     "main":"Nachloch",
                     "score":485,
                     "repicked":"",
                     "burns":6,
                     "mode":"1 Ally",
                     "perks":"Taxer, Pillager, Apprentice, Warrior",
                     "team":4,
                     "hero":"Lord of Hell",
                     "mainsBurnt":1,
                     "repickedTo":"",
                     "gold":6306
                  }
               },
               {
                  "type":22,
                  "id":3,
                  "name":"ArkhanIV#1925",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":true,
                  "isObserver":false,
                  "slot":4,
                  "order":4,
                  "colour":4,
                  "handicap":100,
                  "leftAt":2280,
                  "stayPercent":100,
                  "team":1,
                  "apm":61,
                  "activity":[
                     19,
                     40,
                     42,
                     28,
                     60,
                     59,
                     49,
                     54,
                     48,
                     67,
                     143,
                     80,
                     83,
                     77,
                     151,
                     91,
                     49,
                     93,
                     61,
                     46,
                     49,
                     50,
                     57,
                     61,
                     68,
                     57,
                     55,
                     68,
                     38,
                     48,
                     64,
                     90,
                     59,
                     38,
                     75,
                     55,
                     49,
                     30,
                     1
                  ],
                  "flags":[
                     "winner"
                  ],
                  "variables":{
                     "ally":"Miracle#1425",
                     "heroKills":2,
                     "kills":253,
                     "main":"Tishlak",
                     "score":583,
                     "repicked":"",
                     "burns":6,
                     "mode":"1 Ally",
                     "perks":"Warrior, Recruiter, Forger",
                     "team":5,
                     "hero":"Chief of the Klasgh Clan",
                     "mainsBurnt":3,
                     "repickedTo":"",
                     "gold":6787
                  }
               },
               {
                  "type":22,
                  "id":9,
                  "name":"napå#2146",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":5,
                  "order":5,
                  "colour":5,
                  "handicap":100,
                  "leftAt":425,
                  "stayPercent":18.64,
                  "team":1,
                  "apm":29,
                  "activity":[
                     0,
                     0,
                     19,
                     46,
                     50,
                     35,
                     48
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"SaGe#13105",
                     "heroKills":2,
                     "kills":22,
                     "main":"Horde Hall",
                     "score":52,
                     "repicked":"",
                     "burns":0,
                     "mode":"1 Ally",
                     "perks":"Apprentice, Regenerator, Pillager",
                     "team":6,
                     "hero":"Wretched Captain",
                     "mainsBurnt":0,
                     "repickedTo":"",
                     "gold":1290
                  }
               },
               {
                  "type":22,
                  "id":5,
                  "name":"CatchYaLater#1458",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":6,
                  "order":6,
                  "colour":6,
                  "handicap":100,
                  "leftAt":1955,
                  "stayPercent":85.75,
                  "team":1,
                  "apm":85,
                  "activity":[
                     29,
                     58,
                     95,
                     84,
                     93,
                     86,
                     110,
                     76,
                     89,
                     86,
                     99,
                     95,
                     101,
                     71,
                     88,
                     93,
                     85,
                     93,
                     77,
                     99,
                     77,
                     122,
                     81,
                     84,
                     72,
                     78,
                     65,
                     62,
                     115,
                     83,
                     100,
                     110,
                     24
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"FluffDynasty#1230",
                     "heroKills":1,
                     "kills":198,
                     "main":"Hardak",
                     "score":423,
                     "repicked":"",
                     "burns":7,
                     "mode":"1 Ally",
                     "perks":"Warrior, Protector, Recruiter",
                     "team":7,
                     "hero":"Ice Mage",
                     "mainsBurnt":0,
                     "repickedTo":"",
                     "gold":5503
                  }
               },
               {
                  "type":22,
                  "id":7,
                  "name":"SaGe#13105",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":7,
                  "order":7,
                  "colour":7,
                  "handicap":100,
                  "leftAt":1232,
                  "stayPercent":54.04,
                  "team":1,
                  "apm":83,
                  "activity":[
                     29,
                     49,
                     74,
                     103,
                     54,
                     105,
                     153,
                     87,
                     69,
                     88,
                     89,
                     84,
                     64,
                     117,
                     76,
                     74,
                     106,
                     102,
                     135,
                     59,
                     24
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"napå#2146",
                     "heroKills":0,
                     "kills":90,
                     "main":"Laknash",
                     "score":90,
                     "repicked":"",
                     "burns":0,
                     "mode":"1 Ally",
                     "perks":"Pillager, Voodoo, Warrior",
                     "team":6,
                     "hero":"The Furbolg Mediator",
                     "mainsBurnt":0,
                     "repickedTo":"",
                     "gold":2240
                  }
               },
               {
                  "type":22,
                  "id":6,
                  "name":"FluffDynasty#1230",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":8,
                  "order":8,
                  "colour":8,
                  "handicap":100,
                  "leftAt":2203,
                  "stayPercent":96.62,
                  "team":1,
                  "apm":75,
                  "activity":[
                     53,
                     36,
                     71,
                     65,
                     109,
                     77,
                     139,
                     91,
                     91,
                     122,
                     111,
                     108,
                     121,
                     79,
                     82,
                     73,
                     106,
                     37,
                     49,
                     84,
                     115,
                     69,
                     120,
                     47,
                     125,
                     135,
                     129,
                     99,
                     78,
                     50,
                     0,
                     0,
                     0,
                     0,
                     0,
                     0
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"CatchYaLater#1458",
                     "heroKills":1,
                     "kills":159,
                     "main":"Dalask",
                     "score":324,
                     "repicked":"",
                     "burns":5,
                     "mode":"1 Ally",
                     "perks":"Taxer, Warrior",
                     "team":7,
                     "hero":"Incarnation of Erebus",
                     "mainsBurnt":0,
                     "repickedTo":"",
                     "gold":4537
                  }
               },
               {
                  "type":22,
                  "id":10,
                  "name":"Miracle#1425",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":true,
                  "isObserver":false,
                  "slot":9,
                  "order":9,
                  "colour":9,
                  "handicap":100,
                  "leftAt":2280,
                  "stayPercent":100,
                  "team":1,
                  "apm":119,
                  "activity":[
                     73,
                     116,
                     118,
                     96,
                     152,
                     128,
                     109,
                     108,
                     99,
                     136,
                     141,
                     141,
                     141,
                     187,
                     125,
                     145,
                     144,
                     166,
                     183,
                     87,
                     122,
                     136,
                     104,
                     108,
                     120,
                     143,
                     134,
                     172,
                     124,
                     104,
                     167,
                     95,
                     99,
                     88,
                     122,
                     98,
                     54,
                     48,
                     6
                  ],
                  "flags":[
                     "winner"
                  ],
                  "variables":{
                     "ally":"ArkhanIV#1925",
                     "heroKills":5,
                     "kills":524,
                     "main":"Vulcanus",
                     "score":1079,
                     "repicked":"",
                     "burns":8,
                     "mode":"1 Ally",
                     "perks":"Taxer, Warrior",
                     "team":5,
                     "hero":"Archmage of Nachloch",
                     "mainsBurnt":6,
                     "repickedTo":"",
                     "gold":11111
                  }
               },
               {
                  "type":22,
                  "id":11,
                  "name":"Bu77man#21643",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":10,
                  "order":10,
                  "colour":10,
                  "handicap":100,
                  "leftAt":405,
                  "stayPercent":17.76,
                  "team":1,
                  "apm":41,
                  "activity":[
                     24,
                     30,
                     56,
                     26,
                     51,
                     67,
                     29
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"None",
                     "heroKills":0,
                     "kills":0,
                     "main":"Trollash, Ballua",
                     "score":0,
                     "repicked":"",
                     "burns":0,
                     "mode":"1 Ally",
                     "perks":"Taxer, Looter, Pillager",
                     "team":11,
                     "hero":"Goblin Techies",
                     "mainsBurnt":0,
                     "repickedTo":"",
                     "gold":1345
                  }
               },
               {
                  "type":22,
                  "id":12,
                  "name":"sumre#2728",
                  "partial":null,
                  "race":1,
                  "isHost":false,
                  "isWinner":false,
                  "isObserver":false,
                  "slot":11,
                  "order":11,
                  "colour":11,
                  "handicap":100,
                  "leftAt":1429,
                  "stayPercent":62.68,
                  "team":1,
                  "apm":102,
                  "activity":[
                     39,
                     122,
                     130,
                     144,
                     105,
                     98,
                     86,
                     129,
                     92,
                     117,
                     107,
                     81,
                     68,
                     105,
                     115,
                     90,
                     99,
                     142,
                     126,
                     88,
                     131,
                     77,
                     104,
                     49
                  ],
                  "flags":[
                     "loser"
                  ],
                  "variables":{
                     "ally":"None",
                     "heroKills":1,
                     "kills":144,
                     "main":"Carcinus",
                     "score":189,
                     "repicked":"",
                     "burns":1,
                     "mode":"1 Ally",
                     "perks":"Recruiter, Looter, Regenerator, Defender, Voodoo",
                     "team":12,
                     "hero":"Lord of Labilia",
                     "mainsBurnt":0,
                     "repickedTo":"",
                     "gold":2782
                  }
               }
            ],
            "randomSeed":2552415320,
            "selectMode":3,
            "startSpots":12,
            "events":[
               {
                  "id":107,
                  "intro":"MMD.Dat",
                  "header":"val:390",
                  "message":"Event mode 1\\ Ally",
                  "type":"event",
                  "eventName":"mode",
                  "event":{
                     "id":107,
                     "intro":"MMD.Dat",
                     "header":"val:27",
                     "message":"DefEvent mode 1 mode {0}",
                     "type":"defEvent",
                     "eventName":"mode",
                     "numParams":"1",
                     "params":[
                        "mode"
                     ],
                     "format":"{0}"
                  },
                  "time":2287,
                  "args":[
                     "1\\ Ally"
                  ]
               },
               {
                  "id":107,
                  "intro":"MMD.Dat",
                  "header":"val:547",
                  "message":"Event mode 1\\ Ally",
                  "type":"event",
                  "eventName":"mode",
                  "event":{
                     "id":107,
                     "intro":"MMD.Dat",
                     "header":"val:27",
                     "message":"DefEvent mode 1 mode {0}",
                     "type":"defEvent",
                     "eventName":"mode",
                     "numParams":"1",
                     "params":[
                        "mode"
                     ],
                     "format":"{0}"
                  },
                  "time":2287,
                  "args":[
                     "1\\ Ally"
                  ]
               }
            ],
            "saver":10,
            "hasW3MMD":true
         },
         "chatlog":[
            {
               "playerId":10,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-camera 3333",
               "time":3
            },
            {
               "playerId":3,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-camera 3400",
               "time":3
            },
            {
               "playerId":12,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-camera 3300",
               "time":3
            },
            {
               "playerId":6,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-camera 3500",
               "time":4
            },
            {
               "playerId":10,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-ally yellow",
               "time":7
            },
            {
               "playerId":3,
               "length":22,
               "flags":32,
               "mode":0,
               "message":"-ally light blue",
               "time":9
            },
            {
               "playerId":11,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-camera 3333",
               "time":13
            },
            {
               "playerId":6,
               "length":17,
               "flags":32,
               "mode":0,
               "message":"-ally green",
               "time":14
            },
            {
               "playerId":7,
               "length":12,
               "flags":32,
               "mode":0,
               "message":"fluff?",
               "time":15
            },
            {
               "playerId":5,
               "length":16,
               "flags":32,
               "mode":0,
               "message":"-ally gray",
               "time":17
            },
            {
               "playerId":6,
               "length":16,
               "flags":32,
               "mode":0,
               "message":"sorry sage",
               "time":20
            },
            {
               "playerId":7,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-camera 3200",
               "time":23
            },
            {
               "playerId":6,
               "length":22,
               "flags":32,
               "mode":0,
               "message":"i promised green",
               "time":23
            },
            {
               "playerId":7,
               "length":10,
               "flags":32,
               "mode":0,
               "message":"cool",
               "time":30
            },
            {
               "playerId":11,
               "length":14,
               "flags":32,
               "mode":0,
               "message":"erm guys",
               "time":32
            },
            {
               "playerId":11,
               "length":12,
               "flags":32,
               "mode":0,
               "message":"im new",
               "time":34
            },
            {
               "playerId":11,
               "length":17,
               "flags":32,
               "mode":0,
               "message":"What to do?",
               "time":38
            },
            {
               "playerId":10,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-give yellow",
               "time":53
            },
            {
               "playerId":3,
               "length":22,
               "flags":32,
               "mode":1,
               "message":"-give light blue",
               "time":59
            },
            {
               "playerId":5,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-camera 3333",
               "time":61
            },
            {
               "playerId":5,
               "length":11,
               "flags":32,
               "mode":0,
               "message":"base?",
               "time":62
            },
            {
               "playerId":11,
               "length":33,
               "flags":32,
               "mode":0,
               "message":"perfect then i know thanks!",
               "time":62
            },
            {
               "playerId":9,
               "length":10,
               "flags":32,
               "mode":0,
               "message":"rsdy",
               "time":134
            },
            {
               "playerId":9,
               "length":10,
               "flags":32,
               "mode":0,
               "message":"east",
               "time":136
            },
            {
               "playerId":7,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"-ally orange",
               "time":141
            },
            {
               "playerId":9,
               "length":16,
               "flags":32,
               "mode":0,
               "message":"-ally pink",
               "time":146
            },
            {
               "playerId":11,
               "length":27,
               "flags":32,
               "mode":0,
               "message":"any1 care to explain?",
               "time":166
            },
            {
               "playerId":3,
               "length":19,
               "flags":32,
               "mode":0,
               "message":"pick one base",
               "time":174
            },
            {
               "playerId":3,
               "length":29,
               "flags":32,
               "mode":0,
               "message":"by the expensive troops",
               "time":178
            },
            {
               "playerId":3,
               "length":58,
               "flags":32,
               "mode":0,
               "message":"get gold from killoing enemy units and burning bases",
               "time":194
            },
            {
               "playerId":3,
               "length":33,
               "flags":32,
               "mode":0,
               "message":"go for the creep dots early",
               "time":204
            },
            {
               "playerId":11,
               "length":18,
               "flags":32,
               "mode":0,
               "message":"ig im screwd",
               "time":324
            },
            {
               "playerId":11,
               "length":17,
               "flags":32,
               "mode":0,
               "message":"got no ally",
               "time":328
            },
            {
               "playerId":12,
               "length":23,
               "flags":32,
               "mode":0,
               "message":"anyone east ally?",
               "time":385
            },
            {
               "playerId":3,
               "length":17,
               "flags":32,
               "mode":1,
               "message":"-light blue",
               "time":519
            },
            {
               "playerId":3,
               "length":20,
               "flags":32,
               "mode":1,
               "message":"-light blue 20",
               "time":523
            },
            {
               "playerId":1,
               "length":8,
               "flags":32,
               "mode":0,
               "message":"gg",
               "time":883
            },
            {
               "playerId":10,
               "length":8,
               "flags":32,
               "mode":0,
               "message":"gg",
               "time":889
            },
            {
               "playerId":10,
               "length":18,
               "flags":32,
               "mode":1,
               "message":"-give yellow",
               "time":1201
            },
            {
               "playerId":3,
               "length":22,
               "flags":32,
               "mode":1,
               "message":"-give light blue",
               "time":1287
            },
            {
               "playerId":10,
               "length":18,
               "flags":32,
               "mode":1,
               "message":"-give yellow",
               "time":1295
            },
            {
               "playerId":3,
               "length":9,
               "flags":32,
               "mode":0,
               "message":"o.O",
               "time":1339
            },
            {
               "playerId":3,
               "length":22,
               "flags":32,
               "mode":1,
               "message":"-give light blue",
               "time":1361
            },
            {
               "playerId":10,
               "length":18,
               "flags":32,
               "mode":1,
               "message":"-give yellow",
               "time":1442
            },
            {
               "playerId":3,
               "length":22,
               "flags":32,
               "mode":1,
               "message":"-give light blue",
               "time":1510
            },
            {
               "playerId":10,
               "length":18,
               "flags":32,
               "mode":1,
               "message":"-give yellow",
               "time":1582
            },
            {
               "playerId":10,
               "length":15,
               "flags":32,
               "mode":1,
               "message":"-gray 159",
               "time":1784
            },
            {
               "playerId":10,
               "length":17,
               "flags":32,
               "mode":1,
               "message":"-yellow 159",
               "time":1787
            },
            {
               "playerId":3,
               "length":22,
               "flags":32,
               "mode":0,
               "message":"not enough wards",
               "time":1835
            },
            {
               "playerId":3,
               "length":8,
               "flags":32,
               "mode":0,
               "message":"gg",
               "time":1921
            },
            {
               "playerId":10,
               "length":8,
               "flags":32,
               "mode":0,
               "message":"gg",
               "time":1927
            },
            {
               "playerId":5,
               "length":12,
               "flags":32,
               "mode":0,
               "message":"GG man",
               "time":1930
            },
            {
               "playerId":3,
               "length":8,
               "flags":32,
               "mode":0,
               "message":"gg",
               "time":2194
            },
            {
               "playerId":10,
               "length":8,
               "flags":32,
               "mode":0,
               "message":"gg",
               "time":2196
            },
            {
               "playerId":8,
               "length":8,
               "flags":32,
               "mode":0,
               "message":"gg",
               "time":2202
            },
            {
               "playerId":10,
               "length":17,
               "flags":32,
               "mode":1,
               "message":"-yellow 157",
               "time":2265
            }
         ]
      },
      "downloads":0,
      "isVoid":false,
      "overridable":true,
      "id":91584
   }
}</Endpoint>
<Endpoint route="/replays/{id}/download" method="GET" description="Download replay file by ID." example="/replays/91584/download">{empty response}</Endpoint>
<Endpoint route="/maps/{name} *" method="GET" description="Get map data by name." example="/maps/Broken%20Alliances" id="/maps">{
   "status":"OK",
   "code":200,
   "queryTime":0.0202,
   "body":{
      "name":"Broken Alliances",
      "approved":false,
      "variants":[
         {
            "name":"Broken Alliances",
            "stats":[
               {
                  "id":83596,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 1",
                     "round":"Global",
                     "mode":"Global"
                  }
               },
               {
                  "id":83641,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 1",
                     "round":"Global",
                     "mode":"0"
                  }
               },
               {
                  "id":83663,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 1",
                     "round":"Global",
                     "mode":"2"
                  }
               },
               {
                  "id":84054,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 1",
                     "round":"Global",
                     "mode":"20"
                  }
               },
               {
                  "id":84523,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 1",
                     "round":"Global",
                     "mode":"3"
                  }
               },
               {
                  "id":85730,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 1",
                     "round":"Global",
                     "mode":"4"
                  }
               },
               {
                  "id":90366,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 2",
                     "round":"Global",
                     "mode":"Global"
                  }
               },
               {
                  "id":90379,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 2",
                     "round":"Global",
                     "mode":"2"
                  }
               },
               {
                  "id":90517,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 2",
                     "round":"Global",
                     "mode":"0"
                  }
               },
               {
                  "id":90592,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 2",
                     "round":"Global",
                     "mode":"20"
                  }
               },
               {
                  "id":91238,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 2",
                     "round":"Global",
                     "mode":"3"
                  }
               },
               {
                  "id":171300,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"Global"
                  }
               },
               {
                  "id":171313,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"2"
                  }
               },
               {
                  "id":171567,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"0"
                  }
               },
               {
                  "id":173208,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"3"
                  }
               },
               {
                  "id":175101,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"20"
                  }
               },
               {
                  "id":177931,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"4"
                  }
               },
               {
                  "id":188979,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"1 Ally"
                  }
               },
               {
                  "id":189028,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"6v6"
                  }
               },
               {
                  "id":189514,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"Random 1 Ally"
                  }
               },
               {
                  "id":190232,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"2 Ally"
                  }
               },
               {
                  "id":190254,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 3",
                     "round":"Global",
                     "mode":"Ffa"
                  }
               },
               {
                  "id":199205,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 4",
                     "round":"Global",
                     "mode":"Global"
                  }
               },
               {
                  "id":199218,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 4",
                     "round":"Global",
                     "mode":"1 Ally"
                  }
               },
               {
                  "id":199386,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 4",
                     "round":"Global",
                     "mode":"Random 1 Ally"
                  }
               },
               {
                  "id":199590,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 4",
                     "round":"Global",
                     "mode":"6v6"
                  }
               },
               {
                  "id":201021,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 4",
                     "round":"Global",
                     "mode":"Ffa"
                  }
               },
               {
                  "id":202850,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 4",
                     "round":"Global",
                     "mode":"2"
                  }
               },
               {
                  "id":204925,
                  "collection":"w3s\\Library\\Document\\Statistics",
                  "key":{
                     "map":"Broken Alliances",
                     "ladder":"Public",
                     "season":"Season 4",
                     "round":"Global",
                     "mode":"2 Ally"
                  }
               }
            ]
         }
      ],
      "clans":[

      ],
      "info":{
         "image":"/assets/background/maps/broken-alliances.jpg",
         "description":"With three scattered squads of mercenaries and three bases at your command, you must construct a plan of action to survive the perils of the conflict-stricken island and crush all opposition.",
         "urls":{
            "discord":"https://discord.gg/My8acN4",
            "download":"https://maps.w3reforged.com/maps/categories/other/broken-alliances"
         }
      },
      "ladders":[
         {
            "id":"Public",
            "key":null,
            "title":"Public",
            "seasons":[
               {
                  "title":"Season 1",
                  "start":"09 October 2018",
                  "end":"07 February 2020 18:49:00",
                  "minVersion":null,
                  "rating":{
                     "system":"w3s\\Library\\Rating\\System\\Elo",
                     "settings":{
                        "default":500,
                        "K":32,
                        "D":400
                     },
                     "leagues":null
                  }
               },
               {
                  "title":"Season 2",
                  "start":"14 April 2020",
                  "end":"31 August 2020 18:08:59",
                  "minVersion":"6.2c",
                  "rating":{
                     "system":"w3s\\Library\\Rating\\System\\Elo",
                     "settings":{
                        "default":500,
                        "K":32,
                        "D":400
                     },
                     "leagues":null
                  }
               },
               {
                  "title":"Season 3",
                  "start":"31 August 2020 18:09:00",
                  "end":"02 January 2021 06:05:59",
                  "minVersion":"6.4a",
                  "rating":{
                     "system":"w3s\\Library\\Rating\\System\\Elo",
                     "settings":{
                        "default":500,
                        "K":32,
                        "D":400
                     },
                     "leagues":null
                  }
               },
               {
                  "title":"Season 4",
                  "start":"02 January 2021 06:06:00",
                  "end":"31 August 2021 18:08:59",
                  "minVersion":"6.4a",
                  "rating":{
                     "system":"w3s\\Library\\Rating\\System\\Elo",
                     "settings":{
                        "default":500,
                        "K":32,
                        "D":400
                     },
                     "leagues":null
                  }
               }
            ]
         }
      ],
      "variables":[
         {
            "name":"team",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"mode",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"placement",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"playerLeave",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"ally",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"state",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"colour",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"bonus",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"isVoid",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"results",
            "display":null,
            "type":null,
            "aggregate":false,
            "order":null,
            "values":null
         },
         {
            "name":"stayTime",
            "display":null,
            "type":null,
            "aggregate":true,
            "order":997,
            "values":null
         },
         {
            "name":"stayPercent",
            "display":null,
            "type":null,
            "aggregate":true,
            "order":998,
            "values":null
         },
         {
            "name":"apm",
            "display":null,
            "type":null,
            "aggregate":true,
            "order":999,
            "values":null
         }
      ],
      "schemes":[
         {
            "flags":3,
            "round":[
               {
                  "key":"Global"
               }
            ],
            "mode":[
               "Global"
            ],
            "groupBy":[
               "{variables.team}",
               "{team}"
            ],
            "sortBy":[
               {
                  "key":"{isWinner}",
                  "order":-1,
                  "aggregate":1
               },
               {
                  "key":"{variables.placement}",
                  "order":1,
                  "aggregate":2
               },
               {
                  "key":"{variables.score}",
                  "order":-1,
                  "aggregate":4
               }
            ]
         },
         {
            "flags":3,
            "round":[
               {
                  "key":"{variables.round}",
                  "prefix":"{variables.round}"
               },
               {
                  "key":"Global"
               }
            ],
            "mode":[
               "{variables.mode}"
            ],
            "groupBy":[
               "{variables.team}",
               "{team}"
            ],
            "sortBy":[
               {
                  "key":"{isWinner}",
                  "order":-1,
                  "aggregate":1
               },
               {
                  "key":"{variables.placement}",
                  "order":1,
                  "aggregate":2
               },
               {
                  "key":"{variables.score}",
                  "order":-1,
                  "aggregate":4
               }
            ]
         }
      ],
      "id":1335
   }
}</Endpoint>
<Endpoint route="/results/{replayId} *" method="GET" description="Get all results from a replay (there may be more than one)." example="/results/4915" id="/results">{
   "status":"OK",
   "code":200,
   "queryTime":0.3219,
   "pagination":{
      "next":1,
      "before":1,
      "first":1,
      "current":1,
      "last":1,
      "perPage":15,
      "totalPages":1,
      "totalItems":2
   },
   "body":[
      {
         "replayId":4915,
         "timestamp":1572139442,
         "key":{
            "map":"Broken Alliances",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":[
               "Global"
            ]
         },
         "scheme":{
            "flags":3,
            "round":[
               {
                  "key":"Global"
               }
            ],
            "mode":[
               "Global"
            ],
            "groupBy":[
               "{variables.team}",
               "{team}"
            ],
            "sortBy":[
               {
                  "key":"{isWinner}",
                  "order":-1,
                  "aggregate":1
               },
               {
                  "key":"{variables.placement}",
                  "order":1,
                  "aggregate":2
               },
               {
                  "key":"{variables.score}",
                  "order":-1,
                  "aggregate":4
               }
            ]
         },
         "teams":[
            {
               "id":"7",
               "isWinner":true,
               "placement":1,
               "rating":762,
               "change":8,
               "players":[
                  {
                     "name":"Elcarim",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"7",
                     "colour":1,
                     "apm":118,
                     "leftAt":3723,
                     "stayPercent":100,
                     "isDrawn":false,
                     "isWinner":true,
                     "score":null,
                     "rating":887,
                     "change":8,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "winner"
                     ],
                     "placement":1
                  },
                  {
                     "name":"Leif",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"7",
                     "colour":6,
                     "apm":195,
                     "leftAt":3723,
                     "stayPercent":100,
                     "isDrawn":false,
                     "isWinner":true,
                     "score":null,
                     "rating":637,
                     "change":8,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "winner"
                     ],
                     "placement":1
                  }
               ]
            },
            {
               "id":"3",
               "isWinner":false,
               "placement":2,
               "rating":586,
               "change":11,
               "players":[
                  {
                     "name":"NapalmDeath",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"3",
                     "colour":0,
                     "apm":85,
                     "leftAt":3694,
                     "stayPercent":99.22,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":570,
                     "change":11,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":2
                  },
                  {
                     "name":"[TDA]Enkei",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"3",
                     "colour":2,
                     "apm":194,
                     "leftAt":3684,
                     "stayPercent":98.95,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":602,
                     "change":11,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":2
                  }
               ]
            },
            {
               "id":"4",
               "isWinner":false,
               "placement":3,
               "rating":552,
               "change":8,
               "players":[
                  {
                     "name":"Carmean",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"4",
                     "colour":3,
                     "apm":52,
                     "leftAt":1643,
                     "stayPercent":44.13,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":552,
                     "change":8,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":3
                  }
               ]
            },
            {
               "id":"9",
               "isWinner":false,
               "placement":4,
               "rating":423,
               "change":10,
               "players":[
                  {
                     "name":"Cow_cower_Cow",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"9",
                     "colour":8,
                     "apm":141,
                     "leftAt":1219,
                     "stayPercent":32.74,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":423,
                     "change":10,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":4
                  }
               ]
            },
            {
               "id":"12",
               "isWinner":false,
               "placement":5,
               "rating":764,
               "change":-10,
               "players":[
                  {
                     "name":"burrgy",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"12",
                     "colour":4,
                     "apm":134,
                     "leftAt":1463,
                     "stayPercent":39.3,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":919,
                     "change":-10,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":5
                  },
                  {
                     "name":"Coke_Light",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"12",
                     "colour":11,
                     "apm":53,
                     "leftAt":1131,
                     "stayPercent":30.38,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":609,
                     "change":-10,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":5
                  }
               ]
            },
            {
               "id":"6",
               "isWinner":false,
               "placement":6,
               "rating":496,
               "change":-4,
               "players":[
                  {
                     "name":"phobophile",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"6",
                     "colour":5,
                     "apm":67,
                     "leftAt":891,
                     "stayPercent":23.93,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":492,
                     "change":-4,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":6
                  },
                  {
                     "name":"Ultraliskblades",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"6",
                     "colour":7,
                     "apm":120,
                     "leftAt":1378,
                     "stayPercent":37.01,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":500,
                     "change":-4,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":6
                  }
               ]
            },
            {
               "id":"10",
               "isWinner":false,
               "placement":7,
               "rating":541,
               "change":-10,
               "players":[
                  {
                     "name":"card48",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"10",
                     "colour":9,
                     "apm":1,
                     "leftAt":3537,
                     "stayPercent":95,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":541,
                     "change":-10,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":7
                  }
               ]
            },
            {
               "id":"11",
               "isWinner":false,
               "placement":8,
               "rating":505,
               "change":-13,
               "players":[
                  {
                     "name":"KingOfJokerz",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"Global"
                     },
                     "team":"11",
                     "colour":10,
                     "apm":59,
                     "leftAt":339,
                     "stayPercent":9.11,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":505,
                     "change":-13,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":8
                  }
               ]
            }
         ],
         "id":"352db4321c0ea128cec0a97d78e9b2b7"
      },
      {
         "replayId":4915,
         "timestamp":1572139442,
         "key":{
            "map":"Broken Alliances",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":[
               "{variables.mode}"
            ]
         },
         "scheme":{
            "flags":3,
            "round":[
               {
                  "key":"{variables.round}",
                  "prefix":"{variables.round}"
               },
               {
                  "key":"Global"
               }
            ],
            "mode":[
               "{variables.mode}"
            ],
            "groupBy":[
               "{variables.team}",
               "{team}"
            ],
            "sortBy":[
               {
                  "key":"{isWinner}",
                  "order":-1,
                  "aggregate":1
               },
               {
                  "key":"{variables.placement}",
                  "order":1,
                  "aggregate":2
               },
               {
                  "key":"{variables.score}",
                  "order":-1,
                  "aggregate":4
               }
            ]
         },
         "teams":[
            {
               "id":"7",
               "isWinner":true,
               "placement":1,
               "rating":737,
               "change":9,
               "players":[
                  {
                     "name":"Elcarim",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"7",
                     "colour":1,
                     "apm":118,
                     "leftAt":3723,
                     "stayPercent":100,
                     "isDrawn":false,
                     "isWinner":true,
                     "score":null,
                     "rating":854,
                     "change":9,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "winner"
                     ],
                     "placement":1
                  },
                  {
                     "name":"Leif",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"7",
                     "colour":6,
                     "apm":195,
                     "leftAt":3723,
                     "stayPercent":100,
                     "isDrawn":false,
                     "isWinner":true,
                     "score":null,
                     "rating":620,
                     "change":9,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "winner"
                     ],
                     "placement":1
                  }
               ]
            },
            {
               "id":"3",
               "isWinner":false,
               "placement":2,
               "rating":578,
               "change":12,
               "players":[
                  {
                     "name":"NapalmDeath",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"3",
                     "colour":0,
                     "apm":85,
                     "leftAt":3694,
                     "stayPercent":99.22,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":572,
                     "change":12,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":2
                  },
                  {
                     "name":"[TDA]Enkei",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"3",
                     "colour":2,
                     "apm":194,
                     "leftAt":3684,
                     "stayPercent":98.95,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":584,
                     "change":12,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":2
                  }
               ]
            },
            {
               "id":"4",
               "isWinner":false,
               "placement":3,
               "rating":560,
               "change":8,
               "players":[
                  {
                     "name":"Carmean",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"4",
                     "colour":3,
                     "apm":52,
                     "leftAt":1643,
                     "stayPercent":44.13,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":560,
                     "change":8,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":3
                  }
               ]
            },
            {
               "id":"9",
               "isWinner":false,
               "placement":4,
               "rating":419,
               "change":10,
               "players":[
                  {
                     "name":"Cow_cower_Cow",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"9",
                     "colour":8,
                     "apm":141,
                     "leftAt":1219,
                     "stayPercent":32.74,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":419,
                     "change":10,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":4
                  }
               ]
            },
            {
               "id":"12",
               "isWinner":false,
               "placement":5,
               "rating":788,
               "change":-12,
               "players":[
                  {
                     "name":"burrgy",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"12",
                     "colour":4,
                     "apm":134,
                     "leftAt":1463,
                     "stayPercent":39.3,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":986,
                     "change":-12,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":5
                  },
                  {
                     "name":"Coke_Light",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"12",
                     "colour":11,
                     "apm":53,
                     "leftAt":1131,
                     "stayPercent":30.38,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":589,
                     "change":-12,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":5
                  }
               ]
            },
            {
               "id":"6",
               "isWinner":false,
               "placement":6,
               "rating":497,
               "change":-4,
               "players":[
                  {
                     "name":"phobophile",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"6",
                     "colour":5,
                     "apm":67,
                     "leftAt":891,
                     "stayPercent":23.93,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":493,
                     "change":-4,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":6
                  },
                  {
                     "name":"Ultraliskblades",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"6",
                     "colour":7,
                     "apm":120,
                     "leftAt":1378,
                     "stayPercent":37.01,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":500,
                     "change":-4,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":6
                  }
               ]
            },
            {
               "id":"10",
               "isWinner":false,
               "placement":7,
               "rating":548,
               "change":-10,
               "players":[
                  {
                     "name":"card48",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"10",
                     "colour":9,
                     "apm":1,
                     "leftAt":3537,
                     "stayPercent":95,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":548,
                     "change":-10,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":7
                  }
               ]
            },
            {
               "id":"11",
               "isWinner":false,
               "placement":8,
               "rating":505,
               "change":-13,
               "players":[
                  {
                     "name":"KingOfJokerz",
                     "key":{
                        "map":"Broken Alliances",
                        "ladder":"Public",
                        "season":"Season 1",
                        "round":"Global",
                        "mode":"2"
                     },
                     "team":"11",
                     "colour":10,
                     "apm":59,
                     "leftAt":339,
                     "stayPercent":9.11,
                     "isDrawn":false,
                     "isWinner":false,
                     "score":null,
                     "rating":505,
                     "change":-13,
                     "bonus":1,
                     "variables":[

                     ],
                     "flags":[
                        "loser"
                     ],
                     "placement":8
                  }
               ]
            }
         ],
         "id":"50a4f61fcca456c85ca74df1fd502607"
      }
   ]
}</Endpoint>
<Endpoint route="/profiles/{name} **" method="GET" description="Get directory of foreign profile keys. Each object has an id attribute which can be used in the next request to get the actual profile data." example="/profiles/Anders" id="/profiles">{
   "status":"OK",
   "code":200,
   "queryTime":0.1077,
   "body":[
      {
         "id":1271,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Mazing Contest",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"Global"
         }
      },
      {
         "id":1897,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Warcraft Royale",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"Global"
         }
      },
      {
         "id":5869,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Awlr",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"Global"
         }
      },
      {
         "id":5871,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Awlr",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"Sa Fight"
         }
      },
      {
         "id":7092,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Legion TD Mega",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"Empty"
         }
      },
      {
         "id":11216,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Legion TD",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"Empty"
         }
      },
      {
         "id":11349,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Legion TD",
            "ladder":"LIHL",
            "season":"Season 1",
            "round":"Global",
            "mode":"Empty"
         }
      },
      {
         "id":11735,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Newertest",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"Global"
         }
      },
      {
         "id":89157,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Broken Alliances",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"Global"
         }
      },
      {
         "id":89161,
         "collection":"w3s\\Library\\Document\\Profile",
         "key":{
            "map":"Broken Alliances",
            "ladder":"Public",
            "season":"Season 1",
            "round":"Global",
            "mode":"2"
         }
      }
   ]
}</Endpoint>
<Endpoint route="/profiles/{name}/{profileId} **" method="GET" description="Gets profile details. The profile id comes from the profile foreign key directory (see previous request)." example="/profiles/Anders/89157">{
   "status":"OK",
   "code":200,
   "queryTime":0.0222,
   "body":{
      "name":"Anders",
      "key":{
         "map":"Broken Alliances",
         "ladder":"Public",
         "season":"Season 1",
         "round":"Global",
         "mode":"Global"
      },
      "avgApm":214,
      "avgStay":100,
      "played":1,
      "lastPlayed":1579245173,
      "lastUpdated":1589895931,
      "colours":{
         "4":1
      },
      "results":{
         "2":1
      },
      "wins":0,
      "losses":1,
      "winstreak":0,
      "bestWinstreak":0,
      "winRate":0,
      "rating":515,
      "lastChange":15,
      "rank":441,
      "rankTotal":3036,
      "id":89157,
      "stats":[
         {
            "id":89439,
            "collection":"w3s\\Library\\Document\\Statistics",
            "key":{
               "map":"Broken Alliances",
               "ladder":"Public",
               "season":"Season 1",
               "round":"Global",
               "mode":"Global"
            }
         }
      ],
      "percentile":85.47,
      "allies":[
         {
            "name":"Ultimate_LeBron",
            "playedWith":1,
            "playedAgainst":0,
            "wonWith":0,
            "wonAgainst":0,
            "lostWith":1,
            "lostAgainst":0
         }
      ],
      "rivals":[
         {
            "name":"Wrath_Asuna",
            "playedWith":0,
            "playedAgainst":1,
            "wonWith":0,
            "wonAgainst":0,
            "lostWith":0,
            "lostAgainst":1
         },
         {
            "name":"Bradtheman48",
            "playedWith":0,
            "playedAgainst":1,
            "wonWith":0,
            "wonAgainst":0,
            "lostWith":0,
            "lostAgainst":1
         }
      ]
   }
}</Endpoint>
<Endpoint route="/profiles/{name}/{profileId}/performance **" method="GET" description="Gets player time-series rating history." example="/profiles/Anders/89157/performance">{
   "status":"OK",
   "code":200,
   "queryTime":0.8671,
   "body":[
      {
         "timestamp":1579161600,
         "rating":500,
         "change":15
      }
   ]
}</Endpoint>
<Endpoint route="/leaderboard **" method="GET" description="Gets map leaderboard. <code class='mb-0'>&search=<string> # Search for player name substring.</code>" example="/leaderboard&map=Broken%20Alliances&ladder=Public&season=Season%201&round=Global&mode=2&sort=rank&order=asc&page=1&limit=15" id="/leaderboard">{
   "status":"OK",
   "code":200,
   "queryTime":1.7806,
   "pagination":{
      "next":2,
      "before":1,
      "first":1,
      "current":1,
      "last":186,
      "perPage":15,
      "totalPages":186,
      "totalItems":2783
   },
   "body":[
      {
         "name":"Ultimate_reaper",
         "played":96,
         "wins":68,
         "losses":28,
         "rating":1081,
         "lastChange":9,
         "rank":1
      },
      {
         "name":"[TDA]Miracle",
         "played":120,
         "wins":80,
         "losses":40,
         "rating":1032,
         "lastChange":9,
         "rank":2
      },
      {
         "name":"Polo",
         "played":183,
         "wins":107,
         "losses":76,
         "rating":1024,
         "lastChange":6,
         "rank":3
      },
      {
         "name":"Elcarim",
         "played":124,
         "wins":85,
         "losses":39,
         "rating":1017,
         "lastChange":3,
         "rank":4
      },
      {
         "name":"KyIe",
         "played":122,
         "wins":77,
         "losses":45,
         "rating":972,
         "lastChange":1,
         "rank":5
      },
      {
         "name":"Divine_Doom",
         "played":102,
         "wins":43,
         "losses":59,
         "rating":918,
         "lastChange":10,
         "rank":6
      },
      {
         "name":"[TDA]Hellscream",
         "played":87,
         "wins":52,
         "losses":35,
         "rating":915,
         "lastChange":3,
         "rank":7
      },
      {
         "name":"Witcher",
         "played":69,
         "wins":52,
         "losses":17,
         "rating":912,
         "lastChange":3,
         "rank":8
      },
      {
         "name":"burrgy",
         "played":323,
         "wins":116,
         "losses":207,
         "rating":880,
         "lastChange":-5,
         "rank":9
      },
      {
         "name":"Cloudhunt649",
         "played":147,
         "wins":69,
         "losses":78,
         "rating":875,
         "lastChange":1,
         "rank":10
      },
      {
         "name":"Oil",
         "played":166,
         "wins":54,
         "losses":112,
         "rating":861,
         "lastChange":-2,
         "rank":11
      },
      {
         "name":"[TDA]Blue",
         "played":37,
         "wins":28,
         "losses":9,
         "rating":852,
         "lastChange":8,
         "rank":12
      },
      {
         "name":"SoXu123",
         "played":51,
         "wins":33,
         "losses":18,
         "rating":848,
         "lastChange":8,
         "rank":13
      },
      {
         "name":"Sunstrider44",
         "played":148,
         "wins":41,
         "losses":107,
         "rating":832,
         "lastChange":5,
         "rank":14
      },
      {
         "name":"cotton",
         "played":135,
         "wins":56,
         "losses":79,
         "rating":829,
         "lastChange":-13,
         "rank":15
      }
   ]
}</Endpoint>
<Endpoint route="/stats/{id} **" method="GET" description="Both maps and profile objects contain a list of stats foreign keys. For example: <code class='mb-0'>{
   'id':12,
   'collection':'statistics',
   'key':{
      'map':'Broken Alliances',
      'ladder':'Public',
      'season':'Season 1',
      'round':'Global',
      'mode':'2'
   }
}
</code>" example="/stats/12" id="/stats">{
   "status":"OK",
   "code":200,
   "queryTime":0.004,
   "body":{
      "key":{
         "map":"Battleships Crossfire",
         "ladder":"Public",
         "season":"Season 1",
         "round":"Global",
         "mode":"Global"
      },
      "types":{
         "range":{
            "stayTime":{
               "min":0,
               "max":4750,
               "spread":475,
               "buckets":[
                  {
                     "range":[
                        0,
                        480
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        481,
                        950
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        951,
                        1430
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        1431,
                        1900
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        1901,
                        2380
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        2381,
                        2850
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        2851,
                        3330
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        3331,
                        3800
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        3801,
                        4280
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        4281,
                        4750
                     ],
                     "count":1
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":4745
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":4745
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":4745
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":4745
                  }
               ]
            },
            "stayPercent":{
               "min":0,
               "max":100,
               "spread":10,
               "buckets":[
                  {
                     "range":[
                        0,
                        10
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        11,
                        20
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        21,
                        30
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        31,
                        40
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        41,
                        50
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        51,
                        60
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        61,
                        70
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        71,
                        80
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        81,
                        90
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        91,
                        100
                     ],
                     "count":1
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":100
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":100
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":100
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":100
                  }
               ]
            },
            "apm":{
               "min":0,
               "max":90,
               "spread":9,
               "buckets":[
                  {
                     "range":[
                        0,
                        10
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        11,
                        20
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        21,
                        30
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        31,
                        40
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        41,
                        50
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        51,
                        60
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        61,
                        70
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        71,
                        80
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        81,
                        90
                     ],
                     "count":1
                  },
                  {
                     "range":[
                        91,
                        90
                     ],
                     "count":0
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":85
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":85
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":85
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":85
                  }
               ]
            },
            "kills":{
               "min":0,
               "max":10,
               "spread":1,
               "buckets":[
                  {
                     "range":[
                        0,
                        1
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        1,
                        2
                     ],
                     "count":1
                  },
                  {
                     "range":[
                        2,
                        3
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        3,
                        4
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        4,
                        5
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        5,
                        6
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        6,
                        7
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        7,
                        8
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        8,
                        9
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        9,
                        10
                     ],
                     "count":0
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":2
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":2
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":2
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":2
                  }
               ]
            },
            "Gold Gathered":{
               "min":0,
               "max":61930,
               "spread":6193,
               "buckets":[
                  {
                     "range":[
                        0,
                        6200
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        6201,
                        12390
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        12391,
                        18580
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        18581,
                        24780
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        24781,
                        30970
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        30971,
                        37160
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        37161,
                        43360
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        43361,
                        49550
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        49551,
                        55740
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        55741,
                        61930
                     ],
                     "count":1
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":61930
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":61930
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":61930
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":61930
                  }
               ]
            },
            "assists":{
               "min":0,
               "max":10,
               "spread":1,
               "buckets":[
                  {
                     "range":[
                        0,
                        1
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        1,
                        2
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        2,
                        3
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        3,
                        4
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        4,
                        5
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        5,
                        6
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        6,
                        7
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        7,
                        8
                     ],
                     "count":1
                  },
                  {
                     "range":[
                        8,
                        9
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        9,
                        10
                     ],
                     "count":0
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":8
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":8
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":8
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":8
                  }
               ]
            },
            "Dodos Found":{
               "min":0,
               "max":10,
               "spread":1,
               "buckets":[
                  {
                     "range":[
                        0,
                        1
                     ],
                     "count":1
                  },
                  {
                     "range":[
                        1,
                        2
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        2,
                        3
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        3,
                        4
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        4,
                        5
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        5,
                        6
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        6,
                        7
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        7,
                        8
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        8,
                        9
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        9,
                        10
                     ],
                     "count":0
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":0
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":0
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":0
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":0
                  }
               ]
            },
            "Lumber Gathered":{
               "min":0,
               "max":10,
               "spread":1,
               "buckets":[
                  {
                     "range":[
                        0,
                        1
                     ],
                     "count":1
                  },
                  {
                     "range":[
                        1,
                        2
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        2,
                        3
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        3,
                        4
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        4,
                        5
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        5,
                        6
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        6,
                        7
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        7,
                        8
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        8,
                        9
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        9,
                        10
                     ],
                     "count":0
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":0
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":0
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":0
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":0
                  }
               ]
            },
            "Creep Kills":{
               "min":0,
               "max":990,
               "spread":99,
               "buckets":[
                  {
                     "range":[
                        0,
                        100
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        101,
                        200
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        201,
                        300
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        301,
                        400
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        401,
                        500
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        501,
                        600
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        601,
                        700
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        701,
                        800
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        801,
                        900
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        901,
                        990
                     ],
                     "count":1
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":990
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":990
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":990
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":990
                  }
               ]
            },
            "deaths":{
               "min":0,
               "max":10,
               "spread":1,
               "buckets":[
                  {
                     "range":[
                        0,
                        1
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        1,
                        2
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        2,
                        3
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        3,
                        4
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        4,
                        5
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        5,
                        6
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        6,
                        7
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        7,
                        8
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        8,
                        9
                     ],
                     "count":1
                  },
                  {
                     "range":[
                        9,
                        10
                     ],
                     "count":0
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":9
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":9
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":9
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":9
                  }
               ]
            },
            "bounty":{
               "min":0,
               "max":9930,
               "spread":993,
               "buckets":[
                  {
                     "range":[
                        0,
                        1000
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        1001,
                        1990
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        1991,
                        2980
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        2981,
                        3980
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        3981,
                        4970
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        4971,
                        5960
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        5961,
                        6960
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        6961,
                        7950
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        7951,
                        8940
                     ],
                     "count":0
                  },
                  {
                     "range":[
                        8941,
                        9930
                     ],
                     "count":1
                  }
               ],
               "cardinality":1,
               "timeline":{
                  "W28Y2019":{
                     "cardinality":1,
                     "value":9927
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":9927
                  }
               ],
               "average":{
                  "cardinality":1,
                  "value":9927
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":9927
                  }
               ]
            }
         },
         "grouped":{
            "shiplist":{
               "buckets":{
                  "Schooner":{
                     "losses":1
                  },
                  "Battleturtle":{
                     "losses":1
                  }
               },
               "cardinality":2,
               "timeline":{
                  "Schooner":{
                     "W28Y2019":1
                  },
                  "Battleturtle":{
                     "W28Y2019":1
                  }
               },
               "lowest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":"Schooner"
                  },
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":"Battleturtle"
                  }
               ],
               "average":{
                  "cardinality":2,
                  "value":0
               },
               "highest":[
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":"Schooner"
                  },
                  {
                     "from":{
                        "replayId":13,
                        "player":"fiberoptik"
                     },
                     "value":"Battleturtle"
                  }
               ]
            }
         }
      },
      "id":12
   }
}</Endpoint>
<Endpoint route="/compare/{p1}/{p2} **" method="GET" description="Compare one player to another." example="/compare/Fyre/Emi1&map=Legion%20TD&ladder=LIHL" id="/compare">{
   "status":"OK",
   "code":200,
   "queryTime":0.0431,
   "body":[
      {
         "name":"Emi1",
         "rank":2,
         "rating":1200,
         "playedWith":3,
         "playedAgainst":6,
         "wonWith":3,
         "wonAgainst":4,
         "lostWith":0,
         "lostAgainst":2
      },
      {
         "name":"Fyre",
         "rank":5,
         "rating":1127,
         "playedWith":3,
         "playedAgainst":6,
         "wonWith":3,
         "wonAgainst":2,
         "lostWith":0,
         "lostAgainst":4
      }
   ]
}</Endpoint>

      </Panel2>

      <Panel2 class="flex-grow-1 Article" v-if="$route.params.page == 'w3mmd'">
        <h2>W3MMD Guide</h2>

        <p>W3MMD is a JASS library developed by Strilanc which enables map editors to save custom data to replay files. wc3stats.com relies on this library to reliably determine game winners, teams, scores, and more.</p>

        <h3 id="installation">Installation</h3>

        <p>
          Copy and paste the W3MMD library as a JASS trigger into your map. <br><br>

          <strong class="mr-3">JASS:</strong> <a href="https://wc3stats.com/assets/w3mmd.txt" target="_blank">https://wc3stats.com/assets/w3mmd.txt</a> <br>

          <ol>
            <li>Create a new trigger.</li>
            <li>Select the trigger and click "Edit > Convert" to custom text.</li>
            <li>Replace the text in the trigger with the contents of w3mmd.txt.</li>
          </ol>

          <strong class="mr-3">Typescript:</strong> <a href="https://www.npmjs.com/package/w3ts-w3mmd" target="_blank">https://www.npmjs.com/package/w3ts-w3mmd</a> <br><br>

          Example typescript usage: <a href="https://gist.github.com/Promises/0b5c91a14312bb6da7c248edd4a3ab22" target="_blank">https://gist.github.com/Promises/0b5c91a14312bb6da7c248edd4a3ab22</a> <br><br>
        
          <strong class="mr-3">C#:</strong> <a href="https://github.com/Orden4/WCSharp" target="_blank">https://github.com/Orden4/WCSharp</a> <br><br>
          
          Example C# usage: <a href="https://github.com/Orden4/WCSharp/wiki/WCSharp.W3MMD" target="_blank">https://github.com/Orden4/WCSharp/wiki/WCSharp.W3MMD</a>
        </p>

        <h3 id="variables">Variables</h3>

        <p>There are three types of W3MMD variables to choose from.</p>

        <h4 id="flags">Flags</h4>

        <p>
          Flags save the state of a player, for example "Winner", "Loser", "Draw", etc.<br><br>
          Flagging players simply requires a call to:

          <code>public function FlagPlayer takes player p, integer flag_type returns nothing</code>

          where <mark>flag_type</mark> is one of: <mark>MMD_FLAG_DRAWER</mark>, <mark>MMD_FLAG_LOSER</mark>, <mark>MMD_FLAG_WINNER</mark>, <mark>MMD_FLAG_LEAVER</mark> or <mark>MMD_FLAG_PRACTICING</mark>. Note that wc3stats currently only uses <mark>MMD_FLAG_WINNER</mark> and <mark>MMD_FLAG_LOSER</mark> to determine team placements.<br><br>

          For example, when a player leaves you might use:

          <code>call MMD_FlagPlayer (GetTriggerPlayer (), MMD_FLAG_LOSER)</code>
        </p>

        <h4 id="values">Values</h4>

        <p>
          Values can be anything from the game mode, amount of kills, number hero kills, gold, lumber or any other custom value that you can store in an Integer, String or Real.<br><br>

          Values must be defined using <mark>MMD_DefineValue</mark>:

          <code>public function DefineValue takes string name, integer value_type, integer goal_type, integer suggestion_type returns nothing</code>

          For example:

          <code>call MMD_DefineValue ("team",  MMD_TYPE_INT, MMD_GOAL_NONE, MMD_SUGGEST_NONE)</code>

          <code>call MMD_DefineValue ("items", MMD_TYPE_STRING, MMD_GOAL_NONE, MMD_SUGGEST_TRACK)</code>

          <code>call MMD_DefineValue ("score", MMD_TYPE_REAL, MMD_GOAL_NONE, MMD_SUGGEST_LEADERBOARD)</code>

          Note that <mark>goal_type</mark> and <mark>suggestion_type</mark> are not currently used by wc3stats. <br><br>

          After the values have been defined, they can be updated at any time using the following functions:

          <code>public function UpdateValueInt takes string name, player p, integer op, integer value returns nothing</code>

          <code>public function UpdateValueReal takes string name, player p, integer op, real value returns nothing</code>

          <code>public function UpdateValueString takes string name, player p, string value returns nothing</code>

          For example:

          <code>call MMD_UpdateValueInt ("heroKills", GetEnumPlayer (), MMD_OP_SET, udg_DataHeroKills [GetConvertedPlayerId (GetEnumPlayer ())] )</code>

          Where GetEnumPlayer () refers to the current iterated player, and udg_DataHeroKills is the name of the global GUI variable that you stored your stats in. Just replace those for the names you are using. If you are unfamiliar with JASS, you can add them in GUI and then convert it to custom text. <br><br>

          It is highly recommended that you only update all values once at the end of the game. This means that you should store the values in a global GUI variable and then on a victory condition update values for all players. Every call to an update function adds a new meta action to the replay file and games which are only partially complete may be interpreted by wc3stats as being complete.<br><br>

          It is also recommended to avoid update calls in loops, it can cause occasional loses of data. We have observed that updating each player 1-by-1 with small wait-times in-between has been most effective in ensuring all data is saved properly.<br><br>

          All value variables are available in the Custom Data tab on a wc3stats game profile.
        </p>

        <h4 id="events">Events</h4>

        <p>
          Events can be used to generate a game timeline (for example: <a href="https://wc3stats.com/games/1141/events" target="_blank">https://wc3stats.com/games/1141/events</a>).<br><br>

          Like values, events require initialization via one of the <mark>DefineEvent</mark> functions:

          <code>public function DefineEvent0 takes string name, string format returns nothing</code>

          <code>public function DefineEvent1 takes string name, string format, string argName0 returns nothing</code>

          <code>public function DefineEvent2 takes string name, string format, string argName0, string argName1 returns nothing</code>

          <code>public function DefineEvent3 takes string name, string format, string argName0, string argName1, string argName2 returns nothing</code>

          where the number after "DefineEvent" represents the number of arguments you want to log with the event.<br><br>

          For example, suppose you want to log an event every time a base is destroyed. You might use the following definition:

          <code>MMD_DefineEvent2 ("baseDestroyed", "{0} {1}", "base", "conqueror")</code>

          The format argument ("{0} {1}") denotes the argument number what <mark>LogEvent</mark> is called (basically a string replacement). And to log the event when your base destroyed trigger fires:

          <code>MMD_LogEvent2 ("baseDestroyed", "Name of Base", I2S (GetPlayerId (...)))</code>

          An example implementation for events can be found in <a href="https://github.com/PBug90/w3gPlus/raw/master/maps/(2)EchoIsles_stats.w3x" target="_blank">this map</a>.
        </p>
      </Panel2>
    </div>
  </div>
</template>

<script>

import Banner3 from '@/legacy/components/layout/Banner3';
import Sidebar from '@/legacy/components/Sidebar';
import Scrollbar from '@/legacy/components/Scrollbar';
import Panel2 from '@/legacy/components/widgets/Panel2';
import Endpoint from '@/legacy/components/Endpoint';
import { metaFactory } from '@/legacy/lib//template';

export default {
  metaInfo () {
    return metaFactory ({
      title: 'Documentation',
      description: 'Stay awhile and listen. All the information you need to bring your Warcraft III community to life with an all-you-can-eat buffet of data-driven stats!'
    })
  },

  components: {
    Banner3,
    Sidebar,
    Scrollbar,
    Panel2,
    Endpoint
  }
};

</script>

<style lang="scss" scoped>
</style>